import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faHandHoldingHeart, faPeopleGroup, faBoxOpen, faArrowRight, faTimes, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import OfertarButton from '../common/OfertarButton';

const pillars = [
  {
    icon: faHandHoldingHeart,
    title: 'Cuidado',
    description: 'Cuidar de órfãos, viúvas e vulneráveis é o coração do WeCare — amor que vai além das palavras.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/8',
    border: 'border-rose-500/20',
  },
  {
    icon: faPeopleGroup,
    title: 'Voluntariado',
    description: 'Pessoas que doam seu tempo e talento para servir a comunidade e transformar realidades.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/8',
    border: 'border-yellow-500/20',
  },
  {
    icon: faBoxOpen,
    title: 'Doação',
    description: 'Alimentos, roupas, recursos — cada doação é um ato concreto de amor ao próximo.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/8',
    border: 'border-emerald-500/20',
  },
];

const photos = [
  '/images/wecare_dia_das_maes/wecare_img.jpg',
  '/images/wecare_dia_das_maes/wecare_pastora_patricia.jpg',
  '/images/wecare_dia_das_maes/wecare_voluntarios.jpg',
  '/images/wecare_dia_das_maes/wecare_entrega.jpg',
  '/images/wecare_dia_das_maes/wecare_lanche.jpg',
  '/images/wecare_dia_das_maes/wecare_lenbrancinhas.jpg',
  '/images/wecare_dia_das_maes/wecare_senhora.jpg',
  '/images/wecare_dia_das_maes/wecare_nossa_lider.jpg',
  '/images/wecare_dia_das_maes/wecare_past.jpg',
  '/images/wecare_dia_das_maes/wecare_pastora.jpg',
  '/images/wecare_dia_das_maes/wecare_cantores.jpg',
  '/images/wecare_dia_das_maes/wecare_violino.jpg',
  '/images/wecare_dia_das_maes/wecare_jovem.jpg',
  '/images/wecare_dia_das_maes/wecare_ale.jpg',
  '/images/wecare_dia_das_maes/wecare_destaque_camisa.jpg',
];

const Lightbox = ({ photos, index, onClose, onPrev, onNext }) => {
  if (index === null) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl p-2"
        aria-label="Fechar"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 text-white/70 hover:text-white text-2xl p-3"
        aria-label="Anterior"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <img
        src={photos[index]}
        alt={`WeCare Mon's Day ${index + 1}`}
        className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 text-white/70 hover:text-white text-2xl p-3"
        aria-label="Próxima"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>

      <div className="absolute bottom-4 text-gray-500 text-sm">
        {index + 1} / {photos.length}
      </div>
    </div>
  );
};

const WeCare = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % photos.length);

  return (
    <section id="wecare" className="py-24 bg-[#0d1526]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Cabeçalho */}
        <div className="text-center mb-16">
          <div className="section-label mx-auto w-fit">
            <FontAwesomeIcon icon={faHandHoldingHeart} className="text-xs" /> Ação Social
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            We<span className="text-gradient">Care</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto">
            O braço social da Lagoinha Cidade Verde — porque a fé verdadeira age, serve e transforma.
          </p>
        </div>

        {/* Versículo */}
        <div className="glass-card rounded-2xl p-8 md:p-10 mb-16 relative overflow-hidden">
          <div className="absolute top-5 left-7 text-rose-500/10">
            <FontAwesomeIcon icon={faQuoteLeft} className="text-7xl" />
          </div>
          <p className="relative text-gray-300 text-lg md:text-xl italic text-center max-w-3xl mx-auto leading-relaxed">
            "A religião pura e imaculada diante de Deus Pai é esta: visitar os órfãos e as viúvas nas suas tribulações."
          </p>
          <p className="text-center text-gray-600 text-sm mt-4">— Tiago 1:27</p>
        </div>

        {/* Pilares */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-20">
          {pillars.map(({ icon, title, description, color, bg, border }) => (
            <div
              key={title}
              className={`glass-card card-shine rounded-2xl p-6 border ${border} flex flex-col group hover:bg-white/5 transition-all duration-300 hover:-translate-y-1`}
            >
              <div className={`${bg} w-12 h-12 rounded-xl flex items-center justify-center mb-5`}>
                <FontAwesomeIcon icon={icon} className={`${color} text-lg`} />
              </div>
              <h3 className="text-white font-bold text-lg mb-3">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
            </div>
          ))}
        </div>

        {/* Galeria — Dia das Mães */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h3 className="text-xl font-bold text-white mb-1">
              Nossa última <span className="text-gradient">ação</span>
            </h3>
            <p className="text-gray-600 text-sm">WeCare · Mon's Day</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {photos.map((src, i) => (
              <button
                key={i}
                onClick={() => openLightbox(i)}
                className="relative aspect-square overflow-hidden rounded-xl group focus:outline-none"
              >
                <img
                  src={src}
                  alt={`WeCare Mon's Day ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 rounded-xl" />
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.instagram.com/wecarelagoinhacidadeverde/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-7 py-3.5 rounded-xl text-base font-bold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:-translate-y-0.5"
          >
            <FontAwesomeIcon icon={faInstagram} className="text-lg" />
            Siga o WeCare no Instagram
          </a>
          <a
            href="https://wa.me/5584996632025"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 px-7 py-3.5 rounded-xl text-base font-semibold transition-all duration-200 hover:-translate-y-0.5"
          >
            Quero ser voluntário
            <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
          </a>
          <div className="md:hidden w-full">
            <OfertarButton className="w-full" />
          </div>
        </div>

      </div>

      <Lightbox
        photos={photos}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevPhoto}
        onNext={nextPhoto}
      />
    </section>
  );
};

export default WeCare;
